import { DEFAULT_UPI_REGEX } from '../Withdraw/helper';

/**
 * Validation for the add-mode forms, mirroring Android:
 * - UPI  — `UpiBottomSheet.isUpiIdValid`: config regex, falling back to a
 *          built-in pattern when the config omits it.
 * - Bank — `AddBankAccountFragment.isBankAccValid` / `isIfscValid`: config
 *          regex, and an EMPTY regex means "accept anything", not "reject".
 */

function matches(value: string, pattern?: string): boolean {
  if (!pattern) return true;
  try {
    return new RegExp(pattern).test(value);
  } catch {
    // A malformed regex from config must not block the user.
    console.warn('Invalid withdrawal regex in config:', pattern);
    return true;
  }
}

export function isUpiValid(upiId: string, upiRegex?: string): boolean {
  const trimmed = upiId.trim();
  if (!trimmed) return false;
  return new RegExp(upiRegex || DEFAULT_UPI_REGEX).test(trimmed);
}

export function isAccountNumberValid(
  accountNumber: string,
  bankRegex?: string,
): boolean {
  const trimmed = accountNumber.trim();
  if (!trimmed) return false;
  return matches(trimmed, bankRegex);
}

export function isIfscValid(ifsc: string, ifscRegex?: string): boolean {
  const trimmed = ifsc.trim();
  if (!trimmed) return false;
  return matches(trimmed, ifscRegex);
}

export interface IBankFormValues {
  accountHolderName: string;
  accountNumber: string;
  confirmAccountNumber: string;
  ifscCode: string;
}

export type TBankFieldError = keyof IBankFormValues | null;

/** Returns the first invalid field, in the order Android validates them. */
export function getBankFormError(
  values: IBankFormValues,
  regexes: { bankRegex?: string; ifscRegex?: string },
): TBankFieldError {
  if (!values.accountHolderName.trim()) return 'accountHolderName';
  if (!isAccountNumberValid(values.accountNumber, regexes.bankRegex)) {
    return 'accountNumber';
  }
  if (values.confirmAccountNumber.trim() !== values.accountNumber.trim()) {
    return 'confirmAccountNumber';
  }
  if (!isIfscValid(values.ifscCode, regexes.ifscRegex)) return 'ifscCode';

  return null;
}

export function isBankFormComplete(values: IBankFormValues): boolean {
  return Object.values(values).every((value) => Boolean(value.trim()));
}
